import {
    type ReactElement,
    isValidElement,
    Children,
    useState,
    type FC,
    useRef
} from "react";
import type IPresenceListProps from "./type";
import type {
    PresenceEntry
} from "./type";
import PresenceItem from "./components/item";
import {
    getPresenceChangeCount,
    getPresenceEntries,
    getPresenceIsSame
} from "./util";
import {
    NCoreUIKitTheme
} from "../../core/hooks";

const PresenceList: FC<IPresenceListProps> = ({
    animatedChangeLimit = Infinity,
    isInitialAnimated = false,
    gap: gapProp,
    customTheme,
    axis = "y",
    itemStyle,
    children
}) => {
    const {
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const gap = gapProp ?? spaces.spacingSm;

    const current = Children.toArray(children).filter(isValidElement);

    const initialKeysRef = useRef<Set<string> | null>(null);

    const elementsRef = useRef(new Map<string, ReactElement>());

    const [
        entries,
        setEntries
    ] = useState<Array<PresenceEntry>>(() => current.map((child) => ({
        key: String(child.key),
        isPresent: true
    })));

    if(!initialKeysRef.current) {
        initialKeysRef.current = new Set(isInitialAnimated ? [] : current.map((child) => String(child.key)));
    }

    let nextEntries = getPresenceEntries({
        previous: entries,
        current
    });

    if(getPresenceChangeCount({
        previous: entries,
        next: nextEntries
    }) > animatedChangeLimit) {
        nextEntries = current.map((child) => ({
            key: String(child.key),
            isPresent: true
        }));

        const currentKeys = new Set(nextEntries.map((entry) => entry.key));

        [...elementsRef.current.keys()].forEach((key) => {
            if(!currentKeys.has(key)) {
                elementsRef.current.delete(key);
            }
        });

        currentKeys.forEach((key) => initialKeysRef.current?.add(key));
    }

    if(!getPresenceIsSame({
        previous: entries,
        next: nextEntries
    })) {
        setEntries(nextEntries);
    }

    current.forEach((child) => {
        elementsRef.current.set(String(child.key), child);
    });

    const handleExited = (key: string) => {
        elementsRef.current.delete(key);

        initialKeysRef.current?.delete(key);

        setEntries((previous) => previous.filter((entry) => entry.isPresent || entry.key !== key));
    };

    return <>
        {
            nextEntries.map((entry) => <PresenceItem
                isInitialAnimated={!initialKeysRef.current?.has(entry.key)}
                onExited={() => handleExited(entry.key)}
                isPresent={entry.isPresent}
                customTheme={customTheme}
                style={itemStyle}
                key={entry.key}
                axis={axis}
                gap={gap}
            >
                {elementsRef.current.get(entry.key)}
            </PresenceItem>)
        }
    </>;
};
export default PresenceList;
